/**
 * CAVIAR — cart AOV engine.
 * - Auto-adds/removes the complimentary Caviar Box at the Caviar Unlocked
 *   threshold (eligible subtotal = cart total minus Box lines).
 * - Handles the one-tap upsell add in the cart drawer.
 * - Emits `caviar:*` events for analytics (build spec §12).
 * Config comes from window.CaviarConfig (snippets/caviar-config.liquid).
 */
(function () {
  const cfg = window.CaviarConfig || {};
  if (!cfg.enabled) return;

  const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
  const changeUrl = ((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js';
  const cartUrl = ((window.routes && window.routes.cart_url) || '/cart') + '.js';

  let busy = false;
  let rerun = false;
  let boxBlocked = false; // set when adding the Box fails (e.g. unavailable), to avoid retry loops
  let lastState = null;

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  // Korte bevestiging bij de derde armband (Spec V1.11 §5): "CAVIAR BOX UNLOCKED. ✓"
  function toast(text) {
    let el = document.querySelector('.caviar-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'caviar-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.classList.add('is-visible');
    clearTimeout(el._caviarTimer);
    el._caviarTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 2800);
  }

  function eligibleTotal(cart) {
    let total = cart.total_price;
    cart.items.forEach(function (item) {
      if (item.product_id === cfg.boxProductId) total -= item.final_line_price;
    });
    return total;
  }

  // Stack & Save (spec V3): Box bij 3+ kwalificerende Pieces of een Curated Stack.
  function countPieces(cart) {
    return cart.items.reduce(function (n, item) {
      return (cfg.pieceIds || []).indexOf(item.product_id) !== -1 ? n + item.quantity : n;
    }, 0);
  }

  function hasStack(cart) {
    return cart.items.some(function (item) {
      return (cfg.stackIds || []).indexOf(item.product_id) !== -1;
    });
  }

  function renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const drawer = document.querySelector('cart-drawer');
      const target = drawer && drawer.querySelector('.drawer__inner');
      const fresh = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('.drawer__inner');
      if (target && fresh) {
        target.innerHTML = fresh.innerHTML;
        target.classList.remove('is-empty');
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const fresh = new DOMParser()
          .parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
    }
  }

  async function mutate(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        Object.assign({}, body, {
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        })
      ),
    });
    if (!response.ok) {
      throw new Error('Caviar cart mutation failed (' + response.status + ')');
    }
    const data = await response.json();
    renderSections(data.sections);
    return data;
  }

  async function sync() {
    if (busy) {
      rerun = true;
      return;
    }
    busy = true;
    try {
      const cart = await fetch(cartUrl).then(function (r) {
        return r.json();
      });
      const eligible = eligibleTotal(cart);
      const pieces = countPieces(cart);
      const stackInCart = hasStack(cart);
      const state = {
        eligible: eligible,
        pieces: pieces,
        hasStack: stackInCart,
        shippingUnlocked: stackInCart || eligible >= cfg.thresholdShipping,
        caviarUnlocked: pieces >= 3 || stackInCart,
      };
      if (
        !lastState ||
        lastState.pieces !== state.pieces ||
        lastState.shippingUnlocked !== state.shippingUnlocked ||
        lastState.caviarUnlocked !== state.caviarUnlocked
      ) {
        emit('caviar:milestone', state);
        if (state.caviarUnlocked && (!lastState || !lastState.caviarUnlocked)) {
          emit('caviar:box_unlocked', state);
          if (lastState) toast('Caviar Box unlocked ✓');
        }
      }
      lastState = state;

      if (cfg.boxVariantId) {
        const boxLines = cart.items.filter(function (item) {
          return item.product_id === cfg.boxProductId;
        });
        const hasOtherItems = cart.items.length > boxLines.length;

        if (state.caviarUnlocked && hasOtherItems) {
          if (boxLines.length === 0 && !boxBlocked) {
            try {
              await mutate(addUrl, { items: [{ id: cfg.boxVariantId, quantity: 1 }] });
              emit('caviar:box_added', state);
            } catch (error) {
              boxBlocked = true;
              console.warn('[caviar] could not add Caviar Box:', error);
            }
          } else if (boxLines.length > 0 && boxLines[0].quantity !== 1) {
            await mutate(changeUrl, { id: boxLines[0].key, quantity: 1 });
          }
        } else if (boxLines.length > 0) {
          await mutate(changeUrl, { id: boxLines[0].key, quantity: 0 });
          emit('caviar:box_removed', state);
        }
      }
    } catch (error) {
      console.warn('[caviar] cart sync failed:', error);
    } finally {
      busy = false;
      if (rerun) {
        rerun = false;
        sync();
      }
    }
  }

  let debounceTimer = null;
  function debouncedSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, 300);
  }

  // Re-check after every cart mutation Dawn broadcasts, and once on load.
  // NB: PUB_SUB_EVENTS is een top-level const in constants.js en hangt dus
  // NIET aan window — benader de bare identifier binnen try/catch.
  let cartUpdateEvent = null;
  try {
    cartUpdateEvent = PUB_SUB_EVENTS.cartUpdate;
  } catch (error) {
    cartUpdateEvent = 'cart-update';
  }
  if (typeof subscribe === 'function' && cartUpdateEvent) {
    subscribe(cartUpdateEvent, debouncedSync);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', debouncedSync);
  } else {
    debouncedSync();
  }

  // Reserveringstimer in de cart drawer: start bij het eerste item, loopt 10 min,
  // reset zodra de winkelwagen leeg is. Element wordt bij iedere drawer-render
  // opnieuw gerenderd; de eindtijd staat in localStorage.
  const TIMER_KEY = 'caviar_cart_reserved_until';
  function readTimer() {
    try { return Number(window.localStorage.getItem(TIMER_KEY) || 0); } catch (e) { return 0; }
  }
  function writeTimer(value) {
    try {
      if (value) window.localStorage.setItem(TIMER_KEY, String(value));
      else window.localStorage.removeItem(TIMER_KEY);
    } catch (e) {}
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function tickTimer() {
    const el = document.querySelector('[data-caviar-cart-timer]');
    if (!el) return;
    const minutes = Number(el.dataset.minutes || 10);
    let until = readTimer();
    if (!until || until - Date.now() > minutes * 60000) {
      until = Date.now() + minutes * 60000;
      writeTimer(until);
    }
    const left = Math.max(0, Math.round((until - Date.now()) / 1000));
    const value = el.querySelector('[data-caviar-timer-value]');
    if (value) value.textContent = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
    el.classList.toggle('is-expired', left === 0);
    if (left === 0) {
      el.innerHTML = 'Je reservering is verlopen \u2014 rond je bestelling snel af.';
    }
  }
  setInterval(tickTimer, 1000);
  document.addEventListener('caviar:milestone', function (event) {
    // Lege winkelwagen → timer resetten zodat een volgende sessie opnieuw op 10:00 start.
    if (event.detail && event.detail.pieces === 0 && !event.detail.hasStack && event.detail.eligible === 0) writeTimer(0);
  });

  // Upsell add (event delegation survives drawer re-renders).
  document.addEventListener('click', async function (event) {
    const button = event.target.closest('[data-caviar-upsell-add]');
    if (!button) return;
    event.preventDefault();
    const variantId = Number(button.dataset.caviarUpsellAdd);
    button.setAttribute('disabled', '');
    emit('caviar:upsell_click', { variantId: variantId });
    try {
      await mutate(addUrl, { items: [{ id: variantId, quantity: 1 }] });
      emit('caviar:upsell_added', { variantId: variantId });
      sync();
    } catch (error) {
      button.removeAttribute('disabled');
      console.warn('[caviar] upsell add failed:', error);
    }
  });
})();
